// Route normalisation for analytics: resolve a path against the declared route patterns and emit
// the pattern, with every dynamic position replaced.
//
// Position is what decides, not the word. Classifying segments on their own leaks any value that
// happens to spell a route word — `/q/paid` would keep `paid` even though that position is the
// customer magic-link token, and `/admin/email-templates/new` would keep a human-authored config
// key called `new`. Only the pattern says which positions are values.
//
// Fail closed, twice over: a path matching no pattern is redacted whole, and a position where any
// candidate pattern is dynamic is redacted even if another candidate has a literal there. Every
// segment that survives is copied from ROUTE_PATTERNS, never from the URL, so no substring of the
// visited path can reach analytics.
//
// ROUTE_PATTERNS mirrors routes/router.tsx; routePath.test.ts fails if the two drift apart.

export const REDACTED_SEGMENT = ':id';

const ROUTE_PATTERNS: readonly string[] = [
  '/login',
  '/callback',
  '/q/:token',
  '/q/:token/paid',
  '/pending-activation',
  '/',
  '/how-to',
  '/how-to/:guideId',
  '/whats-new',
  '/analytics',
  '/events',
  '/events/:id',
  '/events/offline',
  '/events/offline/:id',
  '/accounts',
  '/accounts/:id',
  '/quotes',
  '/quotes/new',
  '/quotes/:id',
  '/quotes/:id/edit',
  '/approvals',
  '/team/:userId',
  '/settings',
  '/admin',
  '/admin/integrations',
  '/admin/webhooks',
  '/admin/users',
  '/admin/rules',
  '/admin/audit-log',
  '/admin/catalog',
  '/admin/hubspot',
  '/admin/accounts/ambiguous',
  '/admin/email-templates',
  '/admin/email-templates/:configKey',
  '/admin/diagnostics',
];

const PATTERN_SEGMENTS: readonly string[][] = ROUTE_PATTERNS.map((pattern) => pattern.split('/'));

function isDynamic(segment: string | undefined): boolean {
  return segment !== undefined && segment.startsWith(':');
}

export function normalizeRoutePath(pathname: string): string {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const segments = (trimmed === '' ? '/' : trimmed).split('/');

  // React Router matches case-insensitively, so the lookup does too.
  const candidates = PATTERN_SEGMENTS.filter(
    (pattern) =>
      pattern.length === segments.length &&
      pattern.every(
        (segment, i) => isDynamic(segment) || segment.toLowerCase() === segments[i].toLowerCase(),
      ),
  );

  if (candidates.length === 0) {
    return segments.map((segment) => (segment === '' ? '' : REDACTED_SEGMENT)).join('/');
  }

  return segments
    .map((segment, i) => {
      if (segment === '') return '';
      if (candidates.some((pattern) => isDynamic(pattern[i]))) return REDACTED_SEGMENT;
      return candidates[0][i];
    })
    .join('/');
}
